import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@shared/ui/button";
import { Eye, Search, FilterX } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import {
  useChoices,
  useDeviceManagementMeters,
  useDeviceManagementMetersExport,
} from "@features/mx/device-management/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { GenericTable } from "@shared/components/GenericTable";
import { PAGE_SIZE } from "@shared/utils/constants";
import { TableAction, TableColumn } from "@shared/types/table";

export const PhotoMeters: React.FC = () => {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = Number(searchParams.get("page") || "1");
  const currentSearch = decodeURIComponent(searchParams.get("search") || "");
  const currentStatus = searchParams.get("status") || "all";

  const [pendingFilters, setPendingFilters] = useState({
    status: decodeURIComponent(currentStatus),
  });

  useEffect(() => {
    setPendingFilters({
      status: decodeURIComponent(currentStatus),
    });
  }, [currentStatus]);

  const queryParams = useMemo(
    () => ({
      remoteUtilityId,
      page: currentPage,
      limit: PAGE_SIZE,
      searchData: currentSearch,
      meterType: 0,
      status: currentStatus,
      type: "ALL",
    }),
    [remoteUtilityId, currentPage, currentSearch, currentStatus]
  );

  const {
    data: metersList,
    isLoading,
    refetch,
  } = useDeviceManagementMeters(queryParams);
  const { data: meterStatuses } = useChoices({ choice: "meter_status" });

  useEffect(() => {
    refetch(); // Just refetch the current query
  }, [location.pathname]);

  const handlePendingFilterChange = (value: string) => {
    setPendingFilters({ status: value });
  };

  const handleApplyFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      ...(pendingFilters.status && { status: pendingFilters.status }),
      page: 1,
    };

    setSearchParams(newParams);
  };

  const handleResetFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    delete newParams.status;
    delete newParams.search;
    newParams.page = 1;

    setSearchParams(newParams);
  };

  const handleSearch = (searchTerm: string) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      ...(currentStatus && { status: currentStatus }),
    };
    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search;
    }
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };

    setSearchParams(newParams);
  };

  const { exportCSV, isExporting } = useDeviceManagementMetersExport(
    remoteUtilityId,
    currentSearch,
    currentStatus,
    ""
  );
  const columns: TableColumn<any>[] = [
    { key: "meterNumber", header: "Meter No", sortable: true },
    { key: "deviceNo", header: "Device No", sortable: true },
    { key: "productCode", header: "Utility Service", sortable: true },
    { key: "premiseName", header: "Premise", sortable: true },
    {
      key: "consumerName",
      header: "Consumer Name",
      sortable: true,
      render: (value, row) => {
        const firstName = row?.firstName || "";
        const lastName = row?.lastName || "";
        const fullName = `${firstName} ${lastName}`.trim();
        return fullName || "N/A";
      },
    },
    { key: "consumerNo", header: "Account Number", sortable: true },
    { key: "installedOn", header: "Install Date", sortable: true },
    { key: "status", header: "Status" },
    { key: "meterReading", header: "Last Reading" },
  ];

  const actions: TableAction[] = [
    {
      label: "View",
      icon: "Eye",
      onClick: (row: any) => {
        navigate(`/mx/device-management/meter/view/${row.id}`);
      },
    },
  ];

  const pagination = metersList
    ? {
        currentPage,
        totalPages: Math.ceil(metersList.count / PAGE_SIZE),
        totalItems: metersList.count,
        startItem: (currentPage - 1) * PAGE_SIZE + 1,
        endItem: Math.min(currentPage * PAGE_SIZE, metersList.count),
        itemsPerPage: PAGE_SIZE,
      }
    : undefined;

  const FilterPanel = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Status</label>
          <Select
            value={pendingFilters.status}
            onValueChange={handlePendingFilterChange}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {meterStatuses?.map((status) => (
                <SelectItem key={status.key} value={String(status.key)}>
                  {status.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <Button variant="default" onClick={handleApplyFilters}>
          Apply Filters
        </Button>
        <Button variant="ghost" onClick={handleResetFilters}>
          <FilterX className="w-4 h-4 mr-1" /> Clear
        </Button>
      </div>
    </div>
  );

  const hasActiveFilters = currentStatus !== "all";

  const handleExport = () => {
    exportCSV();
  };

  return (
    <GenericTable
      data={metersList?.results}
      columns={columns}
      rowKey="id"
      actions={actions}
      actionsType="icons"
      pagination={pagination}
      onPageChange={handlePageChange}
      onSearch={handleSearch}
      searchPlaceholder="Search photo meters..."
      searchDebounceMs={500}
      filterPanel={FilterPanel}
      onExport={handleExport}
      exportLoading={isExporting}
      emptyMessage="No photo meters found"
      emptyIcon={<Search className="h-8 w-8 text-muted-foreground mb-2" />}
      filters={
        hasActiveFilters ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Filters active
            </span>
          </div>
        ) : null
      }
    />
  );
};
